import { fetchAgenticSnapshot, type AgenticSnapshot } from "@shared/api/agenticClient";
import {
  activeAccountLabelFromSnapshot,
  availableCashFromActiveAccount,
} from "@shared/ui/agenticPortfolioCash";
import { fmtUsd } from "@shared/ui/watchlistData";

const CASH_CARD_CACHE_MS = 60_000;

let cachedSnapshot: AgenticSnapshot | null = null;
let cachedAt = 0;
let inflight: Promise<void> | null = null;

export function putsTabCashCardHtml(): string {
  return `<div class="sfe-puts-tab-cash-card" data-puts-cash-card role="status" aria-live="polite" data-tip="Spendable cash equivalent on the active Robinhood account (not overnight 2× buying power). Used by the Puts cash-limit filter.">
    <span class="sfe-puts-tab-cash-card__label">Cash</span>
    <span class="sfe-puts-tab-cash-card__value" data-puts-cash-value>…</span>
  </div>`;
}

function paintCashCard(card: HTMLElement, snapshot: AgenticSnapshot | null): void {
  const valueEl = card.querySelector<HTMLElement>("[data-puts-cash-value]");
  if (!valueEl) return;

  if (!snapshot) {
    valueEl.textContent = "—";
    card.dataset.state = "empty";
    card.title = "No Robinhood snapshot — refresh Agentic or Import";
    return;
  }

  const cash = availableCashFromActiveAccount(snapshot);
  const account = activeAccountLabelFromSnapshot(snapshot);
  valueEl.textContent = cash > 0 ? fmtUsd(cash) : fmtUsd(0);
  card.dataset.state = cash > 0 ? "ready" : "zero";
  card.title = account
    ? `${account}: ${cash > 0 ? fmtUsd(cash) : "no spendable cash"}`
    : cash > 0
      ? fmtUsd(cash)
      : "No spendable cash on active account";
}

function findCashCard(strip: ParentNode): HTMLElement | null {
  return strip.querySelector<HTMLElement>("[data-puts-cash-card]");
}

export function refreshPutsTabCashCard(
  strip: HTMLElement | null | undefined,
  opts: { force?: boolean } = {},
): void {
  if (!strip) return;
  const card = findCashCard(strip);
  if (!card) return;

  const force = opts.force === true;
  if (!force && cachedSnapshot && Date.now() - cachedAt < CASH_CARD_CACHE_MS) {
    paintCashCard(card, cachedSnapshot);
    return;
  }

  if (inflight && !force) {
    void inflight.then(() => paintCashCard(card, cachedSnapshot));
    return;
  }

  const valueEl = card.querySelector<HTMLElement>("[data-puts-cash-value]");
  if (valueEl) valueEl.textContent = "…";

  inflight = fetchAgenticSnapshot()
    .then(({ snapshot }) => {
      cachedSnapshot = snapshot;
      cachedAt = Date.now();
      paintCashCard(card, snapshot);
    })
    .catch(() => {
      paintCashCard(card, cachedSnapshot);
    })
    .finally(() => {
      inflight = null;
    });

  void inflight;
}

export function invalidatePutsTabCashCardCache(): void {
  cachedSnapshot = null;
  cachedAt = 0;
  inflight = null;
}
